import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Home } from './pages/Home';
import { Builder } from './pages/Builder';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Dashboard } from './pages/Dashboard';
import { SpinWheelAnalytics } from './pages/SpinWheelAnalytics';
import { Analytics } from './pages/Analytics';
import { Preview } from './pages/Preview';
import { ProfileSettings } from './pages/ProfileSettings';
import { UpgradePlan } from './pages/UpgradePlan';
import { CustomDomain } from './pages/CustomDomain';
import { PaymentCheckout } from './pages/PaymentCheckout';
import { PaymentSuccess } from './pages/PaymentSuccess';
import { PaymentHistory } from './pages/PaymentHistory';
import { VerifyEmail } from './pages/VerifyEmail';
import { FormSubmissions } from './pages/FormSubmissions';
import { SurveyList } from './pages/SurveyList';
import { SurveyResults } from './pages/SurveyResults';
import { ShortLinks } from './pages/ShortLinks';
import { PixelManager } from './pages/PixelManager';
import { TeamSettings } from './pages/TeamSettings';
import { AcceptInvite } from './pages/AcceptInvite';
import { MetaCapi } from './pages/MetaCapi';
import { TagManager } from './pages/TagManager';
import { TrackerPostback } from './pages/TrackerPostback';
import { DevDoc } from './pages/DevDoc';
import { ProtectedRoute } from './components/Common/ProtectedRoute';
import { AdminRoute } from './components/Auth/AdminRoute';
import { AdminDashboard } from './pages/Admin/AdminDashboard';
import { UserManagement } from './pages/Admin/UserManagement';
import { PageManagement } from './pages/Admin/PageManagement';
import { AdminAnalytics } from './pages/Admin/AdminAnalytics';
import { AdminSettings } from './pages/Admin/AdminSettings';
import { AdminSeoSettings } from './pages/Admin/AdminSeoSettings';
import { ArticleList } from './pages/Admin/ArticleList';
import { ArticleEditor } from './pages/Admin/ArticleEditor';
import { FAQList } from './pages/Admin/FAQList';
import { FinancialReports } from './pages/Admin/FinancialReports';
import { ManualInvoices } from './pages/Admin/ManualInvoices';
import { AdminBotCloaking } from './pages/Admin/AdminBotCloaking';
import { AdminPageCloaking } from './pages/Admin/AdminPageCloaking';
import { AdminCloakingRecommendations } from './pages/Admin/AdminCloakingRecommendations';
import { AdminUsagePanel } from './pages/Admin/AdminUsagePanel';
import { AdminBroadcasts } from './pages/Admin/AdminBroadcasts';
import Legal from './pages/Legal';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import { PlatformHead } from './components/Common/PlatformHead';
import { FloatingLineButton } from './components/Common/FloatingLineButton';
import { UpgradeGateModal } from './components/Common/UpgradeGateModal';
import { useAuthStore } from './store/authStore';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const ErrorFallback: React.FC<{ resetError: () => void }> = ({ resetError }) => {
  const { t } = useTranslation('common');
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-8 max-w-md">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('errors.fallbackTitle')}</h2>
        <p className="text-gray-600 mb-6">{t('errors.fallbackBody')}</p>
        <button
          onClick={resetError}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('actions.tryAgain')}
        </button>
      </div>
    </div>
  );
};

function App() {
  const checkAuth = useAuthStore((state) => state.checkAuth);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Keep the JWT fresh for long-lived browser tabs.
  //
  // Why: tokens are issued with a 7-day expiry, and many users keep
  // the dashboard open across days/weeks. Without a refresh loop, the
  // token quietly expires while the tab is open and every subsequent
  // API call returns 401 — the user sees broken stats and "Failed to
  // create page" errors and has to log out + back in to recover.
  //
  // Strategy:
  //   1. On `visibilitychange` (tab returns to foreground) → refresh
  //      eagerly. This is the most common path: the user comes back
  //      after a few hours/a day and we silently roll the token over
  //      before they click anything.
  //   2. As a safety net, also refresh once an hour while the tab is
  //      visible. Cheap, idempotent, and covers the case of a tab
  //      that's been visible the whole time (e.g. a wall display).
  //
  // The actual 401 → logout flow lives in the global fetch
  // interceptor — this hook is only the "preventive maintenance" half.
  useEffect(() => {
    // Throttle so a flurry of visibility events (system idle, alt-tab
    // spam) doesn't hammer /auth/refresh.
    const MIN_INTERVAL_MS = 60_000; // 1 minute
    const HEARTBEAT_MS = 60 * 60 * 1000; // 1 hour
    let lastRefresh = Date.now();

    const tryRefresh = () => {
      const state = useAuthStore.getState();
      if (!state.isAuthenticated || !state.token) return;
      if (Date.now() - lastRefresh < MIN_INTERVAL_MS) return;
      lastRefresh = Date.now();
      void state.refreshToken();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') tryRefresh();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    const heartbeat = window.setInterval(() => {
      if (document.visibilityState === 'visible') tryRefresh();
    }, HEARTBEAT_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearInterval(heartbeat);
    };
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}>
    <HelmetProvider>
      {/* Platform-wide favicon and default SEO */}
      <PlatformHead />
      
      {/* Global CSS for toast progress bar */}
      <style>{`
        /* Add progress bar to all toasts */
        [data-sonner-toast], [data-sonner-toast] > div, div[role="status"] {
          position: relative;
          overflow: visible !important;
        }
        
        div[role="status"]::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: linear-gradient(90deg, #3b82f6, #2563eb);
          animation: shrinkBar 3s linear forwards;
          border-radius: 0 0 8px 8px;
        }
        
        @keyframes shrinkBar {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>

      {/* Simple Toast - แสดงทีละ 1 อัน */}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            paddingBottom: '6px',
          },
        }}
        containerStyle={{
          top: 20,
          right: 20,
        }}
      />

      <Router>
        <SentryRoutes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          
          {/* Legal Pages.
              /legal — combined hub with sidebar tabs (linked from in-app footer).
              /privacy and /terms — dedicated permalinks for Google OAuth
              verification, Apple App Store submission, enterprise procurement
              checklists, etc. They share the same body components as /legal,
              so updates only happen in one place. */}
          <Route path="/legal" element={<Legal />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />

          {/*
            Public Integration Guide for the destination site's dev team.
            Token-gated server-side via the page's postbackSecret;
            no auth wrapper because the audience is third-party
            developers, not platform users.
          */}
          <Route path="/dev/:pageId" element={<DevDoc />} />

        {/* Team invitation acceptance — reachable from the email link.
            The page itself bounces unauthenticated users to /login with a
            redirect back here, so it lives outside ProtectedRoute. */}
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/builder" 
          element={
            <ProtectedRoute>
              <Builder />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/spin-analytics/:pageId/:componentId" 
          element={
            <ProtectedRoute>
              <SpinWheelAnalytics />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/analytics" 
          element={
            <ProtectedRoute>
              <Analytics />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/short-links" 
          element={
            <ProtectedRoute>
              <ShortLinks />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/pixel-manager"
          element={
            <ProtectedRoute>
              <PixelManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team"
          element={
            <ProtectedRoute>
              <TeamSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/meta-capi"
          element={
            <ProtectedRoute>
              <MetaCapi />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tag-manager"
          element={
            <ProtectedRoute>
              <TagManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tracker-postback"
          element={
            <ProtectedRoute>
              <TrackerPostback />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/preview" 
          element={
            <ProtectedRoute>
              <Preview />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <ProfileSettings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/upgrade" 
          element={
            <ProtectedRoute>
              <UpgradePlan />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/addons"
          element={<Navigate to="/upgrade" replace />}
        />
        <Route 
          path="/custom-domain" 
          element={
            <ProtectedRoute>
              <CustomDomain />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/payment-checkout" 
          element={
            <ProtectedRoute>
              <PaymentCheckout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/payment-success" 
          element={<PaymentSuccess />} 
        />
        <Route 
          path="/payment-history" 
          element={
            <ProtectedRoute>
              <PaymentHistory />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/submissions/:pageId" 
          element={
            <ProtectedRoute>
              <FormSubmissions />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/surveys/:pageId" 
          element={
            <ProtectedRoute>
              <SurveyList />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/survey-results/:pageId/:componentId" 
          element={
            <ProtectedRoute>
              <SurveyResults />
            </ProtectedRoute>
          } 
        />
        
        {/* Admin Routes */}
        <Route 
          path="/admin" 
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/users" 
          element={
            <AdminRoute>
              <UserManagement />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/pages" 
          element={
            <AdminRoute>
              <PageManagement />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/analytics" 
          element={
            <AdminRoute>
              <AdminAnalytics />
            </AdminRoute>
          } 
        />
        {/* SEO & Content Management — admin-only.
            The dedicated /admin/seo page hosts JSON-LD inputs that
            used to live in /admin/settings; the older page keeps
            non-SEO platform config (billing, telegram, etc.). */}
        <Route
          path="/admin/seo"
          element={
            <AdminRoute>
              <AdminSeoSettings />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/articles"
          element={
            <AdminRoute>
              <ArticleList />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/articles/:id/edit"
          element={
            <AdminRoute>
              <ArticleEditor />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/faqs"
          element={
            <AdminRoute>
              <FAQList />
            </AdminRoute>
          }
        />
        <Route 
          path="/admin/settings" 
          element={
            <AdminRoute>
              <AdminSettings />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/financial" 
          element={
            <AdminRoute>
              <FinancialReports />
            </AdminRoute>
          } 
        />
        <Route
          path="/admin/invoices"
          element={
            <AdminRoute>
              <ManualInvoices />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/cloaking"
          element={
            <AdminRoute>
              <AdminBotCloaking />
            </AdminRoute>
          }
        />
        {/* Literal `recommendations` route MUST come before the
            dynamic `:pageId` route so React Router doesn't try to
            interpret "recommendations" as a UUID. */}
        <Route
          path="/admin/cloaking/recommendations"
          element={
            <AdminRoute>
              <AdminCloakingRecommendations />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/cloaking/:pageId"
          element={
            <AdminRoute>
              <AdminPageCloaking />
            </AdminRoute>
          }
        />
        {/* Phase 6 — admin ops tooling */}
        <Route
          path="/admin/usage"
          element={
            <AdminRoute>
              <AdminUsagePanel />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/broadcasts"
          element={
            <AdminRoute>
              <AdminBroadcasts />
            </AdminRoute>
          }
        />
      </SentryRoutes>

      {/* Global upgrade-gate modal — pops up whenever the API
          interceptor catches a 4xx with `{ feature, requiredPlan }`,
          or a component calls `openUpgradeGate()` imperatively. Lives
          inside the Router so its <Link> CTA can route correctly. */}
      <UpgradeGateModal />
      </Router>

      <FloatingLineButton />
    </HelmetProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
